import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    Plus,
    Eye,
    Pencil,
    Trash2,
    Search,
    Filter,
    RotateCcw,
    Upload,
    Download,
    X,
    Building2,
    User,
    MapPin,
    Mail,
    Phone,
    Globe
} from "lucide-react";

import * as XLSX from "xlsx";

import "./admin.css";

import {
    getAllPartners,
    deletePartner,
    updatePartnerStatus
} from "../services/PartnerService";


function PartnerLeads() {

    const navigate = useNavigate();


    // =========================================================
    // STATES
    // =========================================================

    const [partners, setPartners] = useState([]);

    const [filteredPartners, setFilteredPartners] = useState([]);

    const [loading, setLoading] = useState(true);


    // =========================================================
    // FILTER STATES
    // =========================================================

    const [search, setSearch] = useState("");

    const [companyType, setCompanyType] = useState("");

    const [city, setCity] = useState("");

    const [status, setStatus] = useState("");


    // =========================================================
    // PAGINATION
    // =========================================================

    const [currentPage, setCurrentPage] = useState(1);

    const recordsPerPage = 6;


    // =========================================================
    // VIEW SIDEBAR STATE
    // =========================================================

    const [viewPartner, setViewPartner] = useState(null);


    // =========================================================
    // NORMALIZE STATUS
    // =========================================================

    const normalizeStatus = (value) => {

        if (!value) {
            return "PENDING";
        }

        return String(value)
            .trim()
            .toUpperCase();

    };


    // =========================================================
    // FORMAT STATUS
    // =========================================================

    const formatStatus = (value) => {

        const statusValue =
            normalizeStatus(value);

        switch (statusValue) {

            case "APPROVED":
                return "Approved";

            case "REJECTED":
                return "Rejected";

            case "ON_HOLD":
                return "On Hold";

            case "PENDING":
            default:
                return "Pending";

        }

    };


    // =========================================================
    // GET STATUS CLASS
    // =========================================================

    const getStatusClass = (value) => {

        const statusValue =
            normalizeStatus(value);

        switch (statusValue) {

            case "APPROVED":
                return "approved";

            case "REJECTED":
                return "rejected";

            case "ON_HOLD":
                return "on-hold";

            case "PENDING":
            default:
                return "pending";

        }

    };


    // =========================================================
    // FETCH ALL PARTNERS
    // =========================================================

    const fetchPartners = async () => {

        try {

            setLoading(true);

            const response =
                await getAllPartners();

            console.log(
                "================================="
            );

            console.log(
                "PARTNER API RESPONSE:"
            );

            console.log(
                response.data
            );

            console.log(
                "================================="
            );


            const apiData =
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.data || [];


            const formattedData =
                apiData.map((partner) => ({

                    ...partner,

                    status:
                        normalizeStatus(
                            partner.status
                        )

                }));


            setPartners(
                formattedData
            );

            setFilteredPartners(
                formattedData
            );


        } catch (error) {

            console.error(
                "PARTNER FETCH ERROR:",
                error
            );

            console.error(
                "SERVER RESPONSE:",
                error.response?.data
            );

            alert(
                error.response?.data?.message ||
                "Unable to load partners."
            );


        } finally {

            setLoading(false);

        }

    };


    // =========================================================
    // INITIAL LOAD
    // =========================================================

    useEffect(() => {

        fetchPartners();

    }, []);


    // =========================================================
    // SIDEBAR: ESC KEY + BODY SCROLL LOCK
    // =========================================================

    useEffect(() => {

        const onKeyDown = (e) => {

            if (e.key === "Escape") {

                setViewPartner(null);

            }

        };

        window.addEventListener("keydown", onKeyDown);

        return () =>
            window.removeEventListener("keydown", onKeyDown);

    }, []);


    useEffect(() => {

        document.body.style.overflow =
            viewPartner ? "hidden" : "";

        return () => {

            document.body.style.overflow = "";

        };

    }, [viewPartner]);


    // =========================================================
    // FILTER LOGIC
    // =========================================================

    useEffect(() => {

        let data = [
            ...partners
        ];


        // =====================================================
        // SEARCH
        // =====================================================

        if (search.trim()) {

            const searchText =
                search
                    .toLowerCase()
                    .trim();


            data = data.filter(
                (partner) =>

                    String(
                        partner.companyName || ""
                    )
                        .toLowerCase()
                        .includes(searchText)

                    ||

                    String(
                        partner.name || ""
                    )
                        .toLowerCase()
                        .includes(searchText)

                    ||

                    String(
                        partner.email || ""
                    )
                        .toLowerCase()
                        .includes(searchText)

                    ||

                    String(
                        partner.mobile || ""
                    )
                        .toLowerCase()
                        .includes(searchText)

                    ||

                    String(
                        partner.gst || ""
                    )
                        .toLowerCase()
                        .includes(searchText)

                    ||

                    String(
                        partner.pan || ""
                    )
                        .toLowerCase()
                        .includes(searchText)

                    ||

                    String(
                        partner.city || ""
                    )
                        .toLowerCase()
                        .includes(searchText)

            );

        }


        // =====================================================
        // COMPANY TYPE
        // =====================================================

        if (companyType) {

            data =
                data.filter(
                    (partner) =>
                        String(
                            partner.companyType || ""
                        ) === companyType
                );

        }


        // =====================================================
        // CITY
        // =====================================================

        if (city.trim()) {

            const cityText =
                city
                    .toLowerCase()
                    .trim();


            data =
                data.filter(
                    (partner) =>
                        String(
                            partner.city || ""
                        )
                            .toLowerCase()
                            .includes(cityText)
                );

        }


        // =====================================================
        // STATUS
        // =====================================================

        if (status) {

            data =
                data.filter(
                    (partner) =>
                        normalizeStatus(
                            partner.status
                        ) ===
                        normalizeStatus(
                            status
                        )
                );

        }


        setFilteredPartners(
            data
        );

        setCurrentPage(1);


    }, [
        search,
        companyType,
        city,
        status,
        partners
    ]);


    // =========================================================
    // PAGINATION
    // =========================================================

    const indexOfLastRecord =
        currentPage *
        recordsPerPage;


    const indexOfFirstRecord =
        indexOfLastRecord -
        recordsPerPage;


    const currentPartners =
        filteredPartners.slice(
            indexOfFirstRecord,
            indexOfLastRecord
        );


    const totalPages =
        Math.ceil(
            filteredPartners.length /
            recordsPerPage
        );


    const changePage = (page) => {

        if (
            page < 1 ||
            page > totalPages
        ) {

            return;

        }

        setCurrentPage(
            page
        );

    };


    // =========================================================
    // CLEAR FILTER
    // =========================================================

    const clearFilter = () => {

        setSearch("");

        setCompanyType("");

        setCity("");

        setStatus("");

        setCurrentPage(1);

    };


    // =========================================================
    // UPDATE PARTNER STATUS
    // =========================================================

    const handleStatusChange = async (
        id,
        newStatus
    ) => {

        const oldPartner =
            partners.find(
                (partner) =>
                    partner.id === id
            );


        const oldStatus =
            oldPartner?.status ||
            "PENDING";


        const statusValue =
            String(newStatus)
                .trim()
                .toUpperCase();


        try {

            console.log(
                "================================="
            );

            console.log(
                "PARTNER STATUS UPDATE"
            );

            console.log(
                "Partner ID:",
                id
            );

            console.log(
                "Old Status:",
                oldStatus
            );

            console.log(
                "New Status:",
                statusValue
            );

            console.log(
                "API:",
                `/api/partners/${id}/status`
            );

            console.log(
                "================================="
            );


            // =================================================
            // API CALL
            // =================================================

            const response =
                await updatePartnerStatus(
                    id,
                    statusValue
                );


            console.log(
                "STATUS UPDATE RESPONSE:",
                response.data
            );


            // =================================================
            // UPDATE FRONTEND
            // =================================================

            setPartners(
                (prev) =>
                    prev.map(
                        (partner) =>
                            partner.id === id
                                ? {
                                    ...partner,
                                    status:
                                        statusValue
                                }
                                : partner
                    )
            );


            alert(
                `Partner status updated to ${formatStatus(
                    statusValue
                )}`
            );


        } catch (error) {

            console.error(
                "================================="
            );

            console.error(
                "PARTNER STATUS UPDATE FAILED"
            );

            console.error(
                "HTTP STATUS:",
                error.response?.status
            );

            console.error(
                "SERVER RESPONSE:",
                error.response?.data
            );

            console.error(
                "ERROR MESSAGE:",
                error.message
            );

            console.error(
                "================================="
            );


            // =================================================
            // RESTORE OLD STATUS
            // =================================================

            setPartners(
                (prev) =>
                    prev.map(
                        (partner) =>
                            partner.id === id
                                ? {
                                    ...partner,
                                    status:
                                        oldStatus
                                }
                                : partner
                    )
            );


            alert(
                error.response?.data?.message ||
                error.response?.data ||
                `Failed to update partner status. HTTP ${
                    error.response?.status || ""
                }`
            );

        }

    };


    // =========================================================
    // DELETE PARTNER
    // =========================================================

    const handleDelete = async (
        id
    ) => {

        const confirmDelete =
            window.confirm(
                "Are you sure you want to delete this partner?"
            );


        if (!confirmDelete) {

            return;

        }


        try {

            console.log(
                "DELETE PARTNER ID:",
                id
            );


            await deletePartner(
                id
            );


            setPartners(
                (prev) =>
                    prev.filter(
                        (partner) =>
                            partner.id !== id
                    )
            );


            setViewPartner(
                (current) =>
                    current && current.id === id
                        ? null
                        : current
            );


            alert(
                "Partner deleted successfully."
            );


        } catch (error) {

            console.error(
                "DELETE PARTNER ERROR:",
                error
            );

            console.error(
                "SERVER RESPONSE:",
                error.response?.data
            );


            alert(
                error.response?.data?.message ||
                error.response?.data ||
                "Failed to delete partner."
            );

        }

    };


    // =========================================================
    // VIEW PARTNER
    // =========================================================

    const handleView = (
        id
    ) => {

        const selectedPartner =
            partners.find(
                (partner) =>
                    partner.id === id
            );


        if (selectedPartner) {

            setViewPartner(
                selectedPartner
            );

        }

    };


    // =========================================================
    // HELPERS FOR SIDEBAR
    // =========================================================

    const getWebsiteUrl = (site) =>
        String(site).startsWith("http")
            ? site
            : `https://${site}`;


    const renderField = (label, value, extraClass = "") => (

        <div className={`pl-field ${extraClass}`}>

            <span className="pl-field-label">
                {label}
            </span>

            <span className="pl-field-value">
                {value || "-"}
            </span>

        </div>

    );


    // =========================================================
    // EDIT PARTNER
    // =========================================================

    const handleEdit = (
        id
    ) => {

        navigate(
            `/partners/edit/${id}`
        );

    };


    // =========================================================
    // EXPORT EXCEL
    // =========================================================

    const handleExport = () => {

        if (
            filteredPartners.length === 0
        ) {

            alert(
                "No partner data available to export."
            );

            return;

        }


        const exportData =
            filteredPartners.map(
                (partner, index) => ({

                    "S.No":
                        index + 1,

                    "Company Name":
                        partner.companyName || "",

                    "Company Type":
                        partner.companyType || "",

                    "Contact Person":
                        partner.name || "",

                    "Designation":
                        partner.designation || "",

                    "Email":
                        partner.email || "",

                    "Mobile":
                        partner.mobile || "",

                    "GST":
                        partner.gst || "",

                    "PAN":
                        partner.pan || "",

                    "Address":
                        partner.address || "",

                    "City":
                        partner.city || "",

                    "State":
                        partner.state || "",

                    "Pincode":
                        partner.pincode || "",

                    "Website":
                        partner.website || "",

                    "Remark":
                        partner.remark || "",

                    "Status":
                        formatStatus(
                            partner.status
                        )

                })
            );


        const worksheet =
            XLSX.utils.json_to_sheet(
                exportData
            );


        const workbook =
            XLSX.utils.book_new();


        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Partners"
        );


        XLSX.writeFile(
            workbook,
            "Partner_Leads.xlsx"
        );

    };


    // =========================================================
    // IMPORT EXCEL
    // =========================================================

    const handleImport = (
        event
    ) => {

        const file =
            event.target.files[0];


        if (!file) {

            return;

        }


        const reader =
            new FileReader();


        reader.onload = (
            e
        ) => {

            try {

                const data =
                    new Uint8Array(
                        e.target.result
                    );


                const workbook =
                    XLSX.read(
                        data,
                        {
                            type: "array"
                        }
                    );


                const sheetName =
                    workbook.SheetNames[0];


                const worksheet =
                    workbook.Sheets[
                        sheetName
                    ];


                const importedData =
                    XLSX.utils.sheet_to_json(
                        worksheet
                    );


                if (
                    importedData.length === 0
                ) {

                    alert(
                        "Excel file is empty."
                    );

                    return;

                }


                const formattedData =
                    importedData.map(
                        (
                            item,
                            index
                        ) => ({

                            id:
                                `import-${Date.now()}-${index}`,

                            companyName:
                                item[
                                    "Company Name"
                                ] || "",

                            companyType:
                                item[
                                    "Company Type"
                                ] || "",

                            name:
                                item[
                                    "Contact Person"
                                ] || "",

                            designation:
                                item[
                                    "Designation"
                                ] || "",

                            email:
                                item[
                                    "Email"
                                ] || null,

                            mobile:
                                item[
                                    "Mobile"
                                ] || "",

                            gst:
                                item[
                                    "GST"
                                ] || null,

                            pan:
                                item[
                                    "PAN"
                                ] || null,

                            address:
                                item[
                                    "Address"
                                ] || "",

                            city:
                                item[
                                    "City"
                                ] || "",

                            state:
                                item[
                                    "State"
                                ] || "",

                            pincode:
                                item[
                                    "Pincode"
                                ] || "",

                            website:
                                item[
                                    "Website"
                                ] || "",

                            remark:
                                item[
                                    "Remark"
                                ] || null,

                            status:
                                normalizeStatus(
                                    item[
                                        "Status"
                                    ]
                                )

                        })
                    );


                setPartners(
                    (prev) => [
                        ...prev,
                        ...formattedData
                    ]
                );


                alert(
                    `${formattedData.length} partner(s) imported successfully.`
                );


            } catch (error) {

                console.error(
                    "Excel Import Error:",
                    error
                );


                alert(
                    "Unable to import Excel file."
                );

            }

        };


        reader.readAsArrayBuffer(
            file
        );


        event.target.value = "";

    };


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div className="partner-page">


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="partner-header">


                <div className="partner-header-left">

                    <div className="partner-title-icon">

                        <Plus size={20} />

                    </div>


                    <div>

                        <h1>
                            Partner Leads
                        </h1>

                        <p>
                            Manage all registered training partners
                        </p>

                    </div>

                </div>


                {/* =================================================
                    HEADER ACTIONS
                ================================================= */}

                <div className="partner-header-actions">


                    {/* IMPORT */}

                    <label
                        className="partner-import-btn"
                    >

                        <Upload size={15} />

                        Import Excel


                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={
                                handleImport
                            }
                            hidden
                        />

                    </label>


                    {/* EXPORT */}

                    <button
                        className="partner-export-btn"
                        onClick={
                            handleExport
                        }
                    >

                        <Download size={15} />

                        Export Excel

                    </button>


                    {/* ADD */}

                    <button
                        className="partner-add-btn"
                        onClick={() =>
                            navigate(
                                "/partner-register"
                            )
                        }
                    >

                        <Plus size={16} />

                        Add Partner

                    </button>

                </div>

            </div>


            {/* =================================================
                FILTER CARD
            ================================================= */}

            <div className="partner-filter-card">


                <div className="partner-filter-title">

                    <div className="partner-filter-icon">

                        <Filter size={16} />

                    </div>


                    <div>

                        <h3>
                            Partner Filters
                        </h3>

                        <span>
                            Search and filter partner leads
                        </span>

                    </div>

                </div>


                <div className="partner-filter-grid">


                    {/* SEARCH */}

                    <div className="partner-search">

                        <Search size={15} />

                        <input
                            type="text"
                            placeholder="Search Company / Name / Email / Mobile"
                            value={search}
                            onChange={(e) =>
                                setSearch(
                                    e.target.value
                                )
                            }
                        />

                    </div>


                    {/* COMPANY TYPE */}

                    <select
                        value={
                            companyType
                        }
                        onChange={(e) =>
                            setCompanyType(
                                e.target.value
                            )
                        }
                    >

                        <option value="">
                            All Company Types
                        </option>

                        <option value="Training Partner">
                            Training Partner
                        </option>

                        <option value="Training Company">
                            Training Company
                        </option>

                        <option value="Corporate">
                            Corporate
                        </option>

                        <option value="Institute">
                            Institute
                        </option>

                        <option value="Consultancy">
                            Consultancy
                        </option>

                        <option value="Other">
                            Other
                        </option>

                    </select>


                    {/* CITY */}

                    <input
                        className="partner-simple-input"
                        type="text"
                        placeholder="Filter by City"
                        value={city}
                        onChange={(e) =>
                            setCity(
                                e.target.value
                            )
                        }
                    />


                    {/* STATUS */}

                    <select
                        value={status}
                        onChange={(e) =>
                            setStatus(
                                e.target.value
                            )
                        }
                    >

                        <option value="">
                            All Status
                        </option>

                        <option value="PENDING">
                            Pending
                        </option>

                        <option value="APPROVED">
                            Approved
                        </option>

                        <option value="REJECTED">
                            Rejected
                        </option>

                        <option value="ON_HOLD">
                            On Hold
                        </option>

                    </select>


                    {/* CLEAR */}

                    <button
                        className="partner-clear-btn"
                        onClick={
                            clearFilter
                        }
                    >

                        <RotateCcw size={14} />

                        Clear

                    </button>

                </div>

            </div>


            {/* =================================================
                TABLE CARD
            ================================================= */}

            <div className="partner-table-card">


                {/* TABLE HEADER */}

                <div className="partner-table-header">


                    <div>

                        <h2>
                            Partner Leads List
                        </h2>

                        <span>
                            Showing{" "}
                            {
                                filteredPartners.length
                            }{" "}
                            partners
                        </span>

                    </div>


                    {/* PAGINATION */}

                    {totalPages > 0 && (

                        <div className="trainer-pagination">

                            <button
                                disabled={
                                    currentPage === 1
                                }
                                onClick={() =>
                                    changePage(
                                        currentPage - 1
                                    )
                                }
                            >
                                Prev
                            </button>


                            {Array.from(
                                {
                                    length:
                                        totalPages
                                },
                                (
                                    _,
                                    index
                                ) => (

                                    <button
                                        key={
                                            index
                                        }
                                        className={
                                            currentPage ===
                                            index + 1
                                                ? "active"
                                                : ""
                                        }
                                        onClick={() =>
                                            changePage(
                                                index + 1
                                            )
                                        }
                                    >

                                        {
                                            index + 1
                                        }

                                    </button>

                                )
                            )}


                            <button
                                disabled={
                                    currentPage ===
                                    totalPages
                                }
                                onClick={() =>
                                    changePage(
                                        currentPage + 1
                                    )
                                }
                            >
                                Next
                            </button>

                        </div>

                    )}

                </div>


                {/* TABLE */}

                <div className="partner-table-wrapper">


                    {loading ? (

                        <div className="partner-empty">

                            Loading partner leads...

                        </div>

                    ) : currentPartners.length === 0 ? (

                        <div className="partner-empty">

                            No Partner Leads Found

                        </div>

                    ) : (

                        <table className="employee-table">


                            <thead>

                                <tr>

                                    <th>
                                        #
                                    </th>

                                    <th>
                                        Company Details
                                    </th>

                                    <th>
                                        Contact Person
                                    </th>

                                    <th>
                                        Address
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th>
                                        Action
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {currentPartners.map(
                                    (
                                        partner,
                                        index
                                    ) => (

                                        <tr
                                            key={
                                                partner.id ||
                                                index
                                            }
                                        >


                                            {/* SERIAL */}

                                            <td>

                                                {
                                                    indexOfFirstRecord +
                                                    index +
                                                    1
                                                }

                                            </td>


                                            {/* COMPANY DETAILS */}

                                            <td>

                                                <div className="partner-company-details">

                                                    <strong className="partner-company-name">

                                                        {
                                                            partner.companyName ||
                                                            "-"
                                                        }

                                                    </strong>


                                                    <span>

                                                        Type:{" "}

                                                        {
                                                            partner.companyType ||
                                                            "-"
                                                        }

                                                    </span>


                                                    <span>

                                                        GST:{" "}

                                                        {
                                                            partner.gst ||
                                                            "-"
                                                        }

                                                    </span>


                                                    <span>

                                                        PAN:{" "}

                                                        {
                                                            partner.pan ||
                                                            "-"
                                                        }

                                                    </span>


                                                    {partner.website && (

                                                        <a
                                                            href={
                                                                String(
                                                                    partner.website
                                                                ).startsWith(
                                                                    "http"
                                                                )
                                                                    ? partner.website
                                                                    : `https://${partner.website}`
                                                            }
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="website-link"
                                                        >

                                                            Visit Website

                                                        </a>

                                                    )}

                                                </div>

                                            </td>


                                            {/* CONTACT PERSON */}

                                            <td>

                                                <div className="partner-contact-details">

                                                    <strong>

                                                        {
                                                            partner.name ||
                                                            "-"
                                                        }

                                                    </strong>


                                                    <span>

                                                        {
                                                            partner.designation ||
                                                            "-"
                                                        }

                                                    </span>


                                                    <span>

                                                        {
                                                            partner.email ||
                                                            "-"
                                                        }

                                                    </span>


                                                    <span>

                                                        {
                                                            partner.mobile ||
                                                            "-"
                                                        }

                                                    </span>

                                                </div>

                                            </td>


                                            {/* ADDRESS */}

                                            <td>

                                                <div className="partner-address-details">

                                                    <span>

                                                        {
                                                            partner.address ||
                                                            "-"
                                                        }

                                                    </span>


                                                    <span>

                                                        {
                                                            partner.city ||
                                                            "-"
                                                        }

                                                        {
                                                            partner.city &&
                                                            partner.state
                                                                ? ", "
                                                                : ""
                                                        }

                                                        {
                                                            partner.state ||
                                                            ""
                                                        }

                                                    </span>


                                                    <small>

                                                        Pincode:{" "}

                                                        {
                                                            partner.pincode ||
                                                            "-"
                                                        }

                                                    </small>

                                                </div>

                                            </td>


                                            {/* STATUS */}

                                            <td>

                                                <select
                                                    className={
                                                        `partner-status-select ${getStatusClass(
                                                            partner.status
                                                        )}`
                                                    }

                                                    value={
                                                        normalizeStatus(
                                                            partner.status
                                                        )
                                                    }

                                                    onChange={(e) =>
                                                        handleStatusChange(
                                                            partner.id,
                                                            e.target.value
                                                        )
                                                    }
                                                >

                                                    <option value="PENDING">
                                                        Pending
                                                    </option>

                                                    <option value="APPROVED">
                                                        Approved
                                                    </option>

                                                    <option value="REJECTED">
                                                        Rejected
                                                    </option>

                                                    <option value="ON_HOLD">
                                                        On Hold
                                                    </option>

                                                </select>

                                            </td>


                                            {/* ACTION */}

                                            <td>

                                                <div className="employee-actions">


                                                    {/* VIEW */}

                                                    <button
                                                        type="button"
                                                        className="small-action-btn view-btn"
                                                        title="View Partner"
                                                        onClick={() =>
                                                            handleView(
                                                                partner.id
                                                            )
                                                        }
                                                    >

                                                        <Eye
                                                            size={14}
                                                        />

                                                    </button>


                                                    {/* EDIT */}

                                                    <button
                                                        type="button"
                                                        className="small-action-btn edit-btn"
                                                        title="Edit Partner"
                                                        onClick={() =>
                                                            handleEdit(
                                                                partner.id
                                                            )
                                                        }
                                                    >

                                                        <Pencil
                                                            size={14}
                                                        />

                                                    </button>


                                                    {/* DELETE */}

                                                    <button
                                                        type="button"
                                                        className="small-action-btn delete-btn"
                                                        title="Delete Partner"
                                                        onClick={() =>
                                                            handleDelete(
                                                                partner.id
                                                            )
                                                        }
                                                    >

                                                        <Trash2
                                                            size={14}
                                                        />

                                                    </button>


                                                </div>

                                            </td>

                                        </tr>

                                    )
                                )}

                            </tbody>

                        </table>

                    )}

                </div>

            </div>

            {/* =================================================
                VIEW PARTNER - RIGHT SIDEBAR
            ================================================= */}

            {viewPartner && (

                <div
                    className="pl-overlay-right"
                    onClick={() =>
                        setViewPartner(null)
                    }
                >

                    <aside
                        className="pl-drawer"
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                    >


                        {/* HEADER */}

                        <div className="pl-modal-header">

                            <div className="pl-modal-title">

                                <div className="pl-modal-avatar">

                                    {
                                        String(
                                            viewPartner.companyName ||
                                            "P"
                                        )
                                            .charAt(0)
                                            .toUpperCase()
                                    }

                                </div>


                                <div>

                                    <h2>
                                        {
                                            viewPartner.companyName ||
                                            "Partner Details"
                                        }
                                    </h2>

                                    <span
                                        className={
                                            `pl-badge ${getStatusClass(
                                                viewPartner.status
                                            )}`
                                        }
                                    >
                                        {
                                            formatStatus(
                                                viewPartner.status
                                            )
                                        }
                                    </span>

                                </div>

                            </div>


                            <button
                                type="button"
                                className="pl-modal-close"
                                title="Close"
                                onClick={() =>
                                    setViewPartner(null)
                                }
                            >

                                <X size={18} />

                            </button>

                        </div>


                        {/* BODY */}

                        <div className="pl-modal-body">


                            {/* COMPANY */}

                            <div className="pl-section">

                                <h4>

                                    <Building2 size={15} />

                                    Company Information

                                </h4>

                                <div className="pl-grid">

                                    {renderField(
                                        "Company Name",
                                        viewPartner.companyName
                                    )}

                                    {renderField(
                                        "Company Type",
                                        viewPartner.companyType
                                    )}

                                    {renderField(
                                        "GST Number",
                                        viewPartner.gst
                                    )}

                                    {renderField(
                                        "PAN Number",
                                        viewPartner.pan
                                    )}

                                    <div className="pl-field full">

                                        <span className="pl-field-label">
                                            Website
                                        </span>

                                        <span className="pl-field-value">

                                            {viewPartner.website ? (

                                                <a
                                                    href={
                                                        getWebsiteUrl(
                                                            viewPartner.website
                                                        )
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="pl-link"
                                                >

                                                    <Globe size={13} />

                                                    {viewPartner.website}

                                                </a>

                                            ) : (
                                                "-"
                                            )}

                                        </span>

                                    </div>

                                </div>

                            </div>


                            {/* CONTACT */}

                            <div className="pl-section">

                                <h4>

                                    <User size={15} />

                                    Contact Person

                                </h4>

                                <div className="pl-grid">

                                    {renderField(
                                        "Name",
                                        viewPartner.name
                                    )}

                                    {renderField(
                                        "Designation",
                                        viewPartner.designation
                                    )}

                                    <div className="pl-field full">

                                        <span className="pl-field-label">
                                            Email
                                        </span>

                                        <span className="pl-field-value">

                                            {viewPartner.email ? (

                                                <a
                                                    href={`mailto:${viewPartner.email}`}
                                                    className="pl-link"
                                                >

                                                    <Mail size={13} />

                                                    {viewPartner.email}

                                                </a>

                                            ) : (
                                                "-"
                                            )}

                                        </span>

                                    </div>

                                    <div className="pl-field full">

                                        <span className="pl-field-label">
                                            Mobile
                                        </span>

                                        <span className="pl-field-value">

                                            {viewPartner.mobile ? (

                                                <a
                                                    href={`tel:${viewPartner.mobile}`}
                                                    className="pl-link"
                                                >

                                                    <Phone size={13} />

                                                    {viewPartner.mobile}

                                                </a>

                                            ) : (
                                                "-"
                                            )}

                                        </span>

                                    </div>

                                </div>

                            </div>


                            {/* ADDRESS */}

                            <div className="pl-section">

                                <h4>

                                    <MapPin size={15} />

                                    Address

                                </h4>

                                <div className="pl-grid">

                                    {renderField(
                                        "Address",
                                        viewPartner.address,
                                        "full"
                                    )}

                                    {renderField(
                                        "City",
                                        viewPartner.city
                                    )}

                                    {renderField(
                                        "State",
                                        viewPartner.state
                                    )}

                                    {renderField(
                                        "Pincode",
                                        viewPartner.pincode
                                    )}

                                </div>

                            </div>


                            {/* REMARK */}

                            <div className="pl-section">

                                <h4>
                                    Remark
                                </h4>

                                <div className="pl-remark">

                                    {
                                        viewPartner.remark ||
                                        "No remark added."
                                    }

                                </div>

                            </div>

                        </div>


                        {/* FOOTER */}

                        <div className="pl-modal-footer">

                            <button
                                type="button"
                                className="pl-btn pl-btn-danger"
                                onClick={() =>
                                    handleDelete(
                                        viewPartner.id
                                    )
                                }
                            >

                                <Trash2 size={14} />

                                Delete

                            </button>


                            <div className="pl-footer-right">

                                <button
                                    type="button"
                                    className="pl-btn pl-btn-outline-dark"
                                    onClick={() =>
                                        setViewPartner(null)
                                    }
                                >
                                    Close
                                </button>


                                <button
                                    type="button"
                                    className="pl-btn pl-btn-primary"
                                    onClick={() =>
                                        handleEdit(
                                            viewPartner.id
                                        )
                                    }
                                >

                                    <Pencil size={14} />

                                    Edit Partner

                                </button>

                            </div>

                        </div>

                    </aside>

                </div>

            )}


        </div>

    );

}


export default PartnerLeads;